/**
 * Entrada de fecha propia, con formato dd/mm/aaaa garantizado.
 *
 * El `<input type="date">` nativo dibuja la fecha segun el locale del SISTEMA
 * OPERATIVO, no del navegador ni de la app: en una maquina en ingles sale
 * `mm/dd/aaaa` y no hay atributo que lo cambie. Eso es lo que hacia que Nucleo
 * se leyera como software importado. Este control resuelve el problema mostrando
 * SIEMPRE `dd/mm/aaaa`, mientras el valor que viaja a la API sigue siendo ISO
 * (`AAAA-MM-DD`): la API no se entera de nada, solo cambia lo que se ve.
 *
 * No reinventa el calendario: el boton de la derecha abre el date picker NATIVO
 * (con su rueda en el celular y su navegacion por teclado, todo ya traducido) a
 * traves de un input oculto y `showPicker()`. Se tipea a mano o se elige del
 * calendario; en los dos caminos el texto visible queda en dd/mm/aaaa.
 */

import { useEffect, useRef, useState } from 'react';

import { controlClass } from '@/components/fields/controls';
import { formatDate, isoFromDMY, maskDMY } from '@/components/fields/format';
import { IconCalendar } from '@/components/shell/icons';

export interface DatePickerInputProps {
  id?: string;
  /** Valor ISO `AAAA-MM-DD` (o vacio). Es lo que guarda y manda la API. */
  value: string;
  /** Emite ISO `AAAA-MM-DD` o `null`; nunca un texto a medio escribir. */
  onChange: (iso: string | null) => void;
  invalid?: boolean;
  required?: boolean;
  describedBy?: string;
  /** Etiqueta accesible del boton de calendario (contexto en el datetime). */
  calendarLabel?: string;
  /** `name` del control visible, para quien agrupa por nombre (el diálogo de acciones). */
  name?: string;
  /**
   * Marca con la que el diálogo de acciones encuentra el primer parámetro para
   * llevarle el foco. Va sobre el input de texto —el que recibe el foco— y no
   * sobre el envoltorio, que no es focusable.
   */
  'data-param-control'?: boolean;
}

export function DatePickerInput({
  id,
  value,
  onChange,
  invalid = false,
  required = false,
  describedBy,
  calendarLabel = 'Abrir calendario',
  name,
  'data-param-control': marcaDeParametro,
}: DatePickerInputProps) {
  const iso = typeof value === 'string' && value !== '' ? value.slice(0, 10) : '';
  const [texto, setTexto] = useState<string>(() => formatDate(iso));
  // Ultimo ISO emitido/conocido: para resincronizar ante cambios externos y para
  // restaurar el texto cuando alguien deja el campo a medio escribir.
  const ultimoIso = useRef<string>(iso);
  const nativoRef = useRef<HTMLInputElement>(null);

  // El valor puede cambiar desde afuera (recarga del registro, rollback de un
  // guardado optimista). Se resincroniza solo si no es lo que acabamos de emitir.
  useEffect(() => {
    if (iso !== ultimoIso.current) {
      ultimoIso.current = iso;
      setTexto(formatDate(iso));
    }
  }, [iso]);

  function emitir(nuevoIso: string | null): void {
    ultimoIso.current = nuevoIso ?? '';
    onChange(nuevoIso);
  }

  function alEscribir(crudo: string): void {
    const enmascarado = maskDMY(crudo);
    setTexto(enmascarado);
    if (enmascarado === '') {
      emitir(null);
      return;
    }
    // Solo se emite cuando ya hay una fecha completa y real: mientras se tipea
    // "28/07/20" no se manda nada a medias.
    const parsed = isoFromDMY(enmascarado);
    if (parsed !== null) emitir(parsed);
  }

  function alSalir(): void {
    if (texto.trim() === '') {
      emitir(null);
      return;
    }
    const parsed = isoFromDMY(texto);
    if (parsed === null) {
      // Fecha incompleta o imposible: se vuelve a lo ultimo valido en vez de
      // dejar un "28/07/20" colgado que no representa ningun valor.
      setTexto(formatDate(ultimoIso.current));
    } else {
      setTexto(formatDate(parsed));
    }
  }

  function abrirCalendario(): void {
    const nativo = nativoRef.current;
    if (nativo === null) return;
    nativo.value = ultimoIso.current;
    if (typeof nativo.showPicker === 'function') {
      try {
        nativo.showPicker();
        return;
      } catch {
        // Algunos navegadores exigen gesto directo; se cae al focus+click de abajo.
      }
    }
    nativo.focus();
    nativo.click();
  }

  return (
    <div className="relative">
      <input
        id={id}
        {...(name === undefined ? {} : { name })}
        {...(marcaDeParametro === undefined ? {} : { 'data-param-control': marcaDeParametro })}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder="dd/mm/aaaa"
        className={controlClass(invalid, 'pr-10 tabular-nums')}
        value={texto}
        required={required}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(evento) => {
          alEscribir(evento.target.value);
        }}
        onBlur={alSalir}
      />
      <button
        type="button"
        onClick={abrirCalendario}
        aria-label={calendarLabel}
        className="absolute inset-y-0 right-0 flex items-center px-3 text-fg-faint transition-colors hover:text-accent focus-visible:text-accent"
      >
        <IconCalendar className="h-4 w-4" />
      </button>
      {/* Input nativo solo para el POPUP del calendario: no se ve ni recibe foco
          por tab, y su unico trabajo es abrir la rueda/almanaque del sistema. */}
      <input
        ref={nativoRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 right-2 h-0 w-0 opacity-0"
        onChange={(evento) => {
          const v = evento.target.value;
          setTexto(v === '' ? '' : formatDate(v));
          emitir(v === '' ? null : v);
        }}
      />
    </div>
  );
}
